import { module } from "modujs";
import { html } from "../utils/environment";
import VueCart from "../vue/components/VueCart";
import VueCartItem from "../vue/components/VueCartItem";
import VueQuantityInput from "../vue/components/VueQuantityInput";
import * as focusTrap from 'focus-trap';

const REFRESH_CART_EVENT = "refreshcart";

export default class extends module {
    constructor(m) {
        super(m);

        // Binding
        this.onEscapeBind = (e) => {
            if (
                e.key === "Escape" &&
                html.classList.contains("has-cart-open")
            ) {
                this.close();
            }
        };

        // Data
        this.isCartModal = (this.el.dataset.moduleCart === "cartModal")
        this.isCartPage = (this.el.dataset.moduleCart === "cartPage")

        // UI
        this.$el = this.el;

        this.cart = null;
    }

    ///////////////
    // Lifecyle
    ///////////////
    async init() {
        this.bindEvents();

        // Initial cart
        this.cart = await this.getCart();

        // Init vue
        this.initVue();

        if (!window.isMobile) {
            // Trap
            this.trap = focusTrap.createFocusTrap([document.querySelector('.c-cart')], {
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                allowOutsideClick: () => true
            });
        }

        // Update cart
        this.updateCart(this.cart);
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
        this.app?.$destroy?.();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        document.addEventListener('keyup', this.onEscapeBind)
    }

    unbindEvents() {
        document.removeEventListener('keyup', this.onEscapeBind)
    }

    ///////////////
    // Display
    toggle() {
        if (html.classList.contains("has-cart-open")) {
            this.close();

            if (this.isCartModal) {
                this.call('getCart', null, 'Cart', 'cartPage')
            }
        } else {
            this.open();

            if (this.isCartModal) {
                this.getCart()
            }
        }
    }

    open() {
        html.classList.add("has-cart-open")

        this.trap?.activate();
    }

    close() {
        html.classList.remove("has-cart-open")

        this.trap?.deactivate();
    }

    ///////////////
    // Methods
    getCart() {
        return new Promise((resolve) => {
            fetch(window.Shopify.routes.root + "cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                resolve(data);
            })
            .catch((error) => {
                console.error("Error:", error);
                resolve(null);
                this.setCartIsUpdating(false);
            });
        });
    }

    // Add to cart
    addToCart({formData, openCart = true, item}) {
        this.setCartIsUpdating(true);

        fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        })
        .then((response) => {
            return response.json();
        })
        .then(async () => {
            this.cart = await this.getCart();

            this.setCartIsUpdating(false);

            // Update cart
            this.updateCart(this.cart);

            if(
                item &&
                window?.Shopify?.customerPrivacy?.marketingAllowed() &&
                window?.Shopify?.customerPrivacy?.analyticsProcessingAllowed()
            ) {
                // Build the array of products in the cart
                var itemList = [];
                this.cart.items.forEach(function (item, i) {
                    var itemObj = new Object();
                    itemObj.variant_id = item['variant_id'];
                    itemObj.sku = item['sku'];
                    itemObj.title = item['product_title'];
                    itemObj.quantity = item['quantity'];
                    itemObj.price = item['price'] / 100;
                    itemObj.RowTotal = item['line_price'] / 100;
                    itemObj.url = 'https://www.flambette.com' + item['url'];
                    itemObj.image = item['featured_image']['url'];
                    itemList.push(itemObj);
                });
                // Push the add to cart event to Klaviyo
                _learnq.push(['track', 'Added to Cart', item]);
                console.log(_learnq, item);
            }

            if(openCart) {
                requestAnimationFrame(() => {
                    this.open()
                })
            }
        })
        .catch((error) => {
            console.error("Error:", error);
            this.setCartIsUpdating(false);
        });
    }

    changeCart(formData) {
        this.setCartIsUpdating(true);

        fetch(window.Shopify.routes.root + "cart/change.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        })
        .then((response) => {
            return response.json();
        })
        .then(async () => {
            this.cart = await this.getCart();

            // Update cart
            this.updateCart(this.cart);

            this.setCartIsUpdating(false);

            window.dispatchEvent(
                new CustomEvent(REFRESH_CART_EVENT, {
                    detail: { cart: this.cart },
                })
            );
        })
        .catch((error) => {
            console.error("Error:", error);
            this.setCartIsUpdating(false);
        });
    }

    updateCart(cart) {
        // Update cart data
        this.app && this.app.$refs.cart && this.app.$refs.cart.setCartData(cart)

        // Update total
        this.call('updateTotal', cart, 'CartTotal')

        // Update cart counter
        this.call('setCartCounter', cart, 'CartCounter')

        // Update cart shipping
        this.call('updateProgress', cart, 'CartShipping')
    }

    setCartIsUpdating(value) {
        if (value) {
            document.documentElement.classList.add("is-cart-updating");
        } else {
            document.documentElement.classList.remove("is-cart-updating");
        }
    }

    /////////////////////////
    // Vue Instance
    initVue() {
        /////////////////////////
        // Save moduleInstance
        const moduleInstance = this

        /////////////////////////
        // Settings
        Vue.config.devtools = true

        /////////////////////////
        // Vue Components
        VueCart(moduleInstance)
        VueCartItem()

        if (!window.commonVueInstancesReady) {
            VueQuantityInput()
            window.commonVueInstancesReady = true
        }

        /////////////////////////
        // Vue Instance
        this.app = new Vue({
            el: this.el
        });
    }
}
